import type { DepartmentPhase, PhaseEntry, PhaseRunnerHooks } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';

/**
 * PhaseRunner — owns the advance/loop/interrupt semantics of a department's
 * phase cycle (see phases.types.ts for the framework contract).
 *
 * Counter semantics mirror the reference stage counters exactly
 * (SpacesDepartment._counter): the counter accumulates the deltaTime the
 * department feeds tick(); at >= the current entry's frames it zeroes and
 * the next phase starts (wrapping fires onCycleComplete). startPhase() does
 * NOT touch the counter — the reference presets it around scene start
 * (counter = 120 on reposition, frames - 90 on the from-work resume), so
 * the department sets it explicitly via setCounter().
 *
 * Transition order (one advance): outgoing.hide() -> hooks.onTransition ->
 * incoming.start() -> hooks.onStarted — the reference's clear-then-arm-then-
 * colour sequence.
 *
 * stop() maps a scene stop to hide() + reset() on the current phase, so the
 * next startAsActiveScene re-forms cleanly (the framework's re-entry rule).
 *
 * resume(index) positions the runner WITHOUT lifecycle calls — the
 * reference's from-work path (hasBeenStarted: the counter just runs on and
 * the NEXT advance arms normally; the resumed phase itself is never
 * re-armed, so its hide() on that advance must tolerate a never-started
 * phase — part of the hide() contract).
 */
export class PhaseRunner {
	private _entries: ReadonlyArray<PhaseEntry>;
	private _hooks: PhaseRunnerHooks;

	private _index = 0;
	private _counter = 0;
	private _durationFrames = 0;
	private _running = false;
	private _totalPasses = 0;

	constructor(entries: ReadonlyArray<PhaseEntry>, hooks: PhaseRunnerHooks = {}) {
		this._entries = entries;
		this._hooks = hooks;
		this._durationFrames = this._resolveFrames(0);
	}

	public get index(): number {
		return this._index;
	}

	public get phase(): DepartmentPhase {
		return this._entries[this._index].phase;
	}

	/** Frames of the CURRENT entry (resolved at its start). */
	public get durationFrames(): number {
		return this._durationFrames;
	}

	/** One-time init() across every phase — call from the department ctor. */
	public initAll(): void {
		for (let i = 0; i < this._entries.length; i++) {
			this._entries[i].phase.init();
		}
	}

	/** Preset the stage counter (the reference's 120 / frames - 90 writes). */
	public setCounter(v: number): void {
		this._counter = v;
	}

	/** Arm a phase now: hide the outgoing one, run the transition hooks,
	 *  start the incoming one. Leaves the counter untouched (see header). */
	public startPhase(index: number): void {
		if (this._running) {
			this._entries[this._index].phase.hide();
		}

		this._hooks.onTransition?.(index, this._entries[index].phase);

		this._index = index;
		this._durationFrames = this._resolveFrames(index);
		this._entries[index].phase.start();
		this._running = true;

		this._hooks.onStarted?.(index, this._entries[index].phase);
	}

	/** Position without lifecycle calls (the from-work resume — see header). */
	public resume(index: number): void {
		this._index = index;
		this._durationFrames = this._resolveFrames(index);
		this._running = true;
	}

	/** Advance the stage counter (frame-normalised deltaTime domain). */
	public tick(deltaTime: number): void {
		if (!this._running) {
			return;
		}

		this._counter += deltaTime;
		if (this._counter >= this._durationFrames) {
			this._counter = 0;

			let next = this._index + 1;
			if (next >= this._entries.length) {
				next = 0;
				this._totalPasses++;
				this._hooks.onCycleComplete?.(this._totalPasses);
			}

			this.startPhase(next);
		}
	}

	/** Scene stop: hide + reset the current phase (clean re-entry). */
	public stop(): void {
		if (this._running) {
			this._entries[this._index].phase.hide();
			this._entries[this._index].phase.reset();
			this._running = false;
		}
	}

	private _resolveFrames(index: number): number {
		const frames = this._entries[index].frames;
		return typeof frames === 'function' ? frames() : frames;
	}
}
